
import React, { createContext, useState, useContext, ReactNode, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { getUserSettings, updateUserSettings } from '../services/firebaseService';
import type { Theme, UserSettings } from '../types';

interface SettingsContextType {
  koreanFontSize: number;
  setKoreanFontSize: (size: number) => void;
  mathFontSize: number;
  setMathFontSize: (size: number) => void;
  lineHeight: number;
  setLineHeight: (height: number) => void;
  theme: Theme;
  setTheme: (theme: Theme) => void;
  isLoading: boolean; // For tracking initial settings load
}

const defaultSettings: UserSettings = {
  koreanFontSize: 14,
  mathFontSize: 14,
  lineHeight: 1.6,
  theme: 'dark',
};

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const SettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [settings, setSettings] = useState<UserSettings>(defaultSettings);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch settings when user logs in or out
  useEffect(() => {
    if (user) {
      setIsLoading(true);
      getUserSettings(user.uid)
        .then(savedSettings => {
          // If settings are found, merge them with defaults to ensure all keys are present
          if (savedSettings) {
            setSettings({ ...defaultSettings, ...savedSettings });
          } else {
            // No settings for this user in Firestore, use defaults
            setSettings(defaultSettings);
          }
        })
        .catch(error => {
          console.error("Failed to fetch user settings:", error);
          setSettings(defaultSettings); // Revert to defaults on error
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      // User is logged out, reset to default settings
      setSettings(defaultSettings);
      setIsLoading(false);
    }
  }, [user]);

  // A generic updater function to update state and Firestore
  const updateSetting = useCallback((key: keyof UserSettings, value: any) => {
    // Update local state immediately for responsiveness
    setSettings(prev => ({ ...prev, [key]: value }));
    
    // Persist to Firestore if user is logged in
    if (user) {
      updateUserSettings(user.uid, { [key]: value });
    }
  }, [user]);

  const value = {
    ...settings,
    setKoreanFontSize: (size: number) => updateSetting('koreanFontSize', size),
    setMathFontSize: (size: number) => updateSetting('mathFontSize', size),
    setLineHeight: (height: number) => updateSetting('lineHeight', height),
    setTheme: (theme: Theme) => updateSetting('theme', theme),
    isLoading,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};
